import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';

export interface CollectError {
  collect_id: string;
  planned_date: string;
  sla_status: string | null;
  kind_of_material: string | null;
  skip_reason: string;
  created_at: string;
  establishment_name: string | null;
}

export interface CollectDivergence {
  collect_id: string;
  planned_date: string;
  kind_of_material: string | null;
  establishment_name: string | null;
  current_status: string | null;
  expected_status: string | null;
  current_weight: number | null;
  expected_weight: number | null;
  current_volume: number | null;
  expected_volume: number | null;
  current_units: number | null;
  expected_units: number | null;
  divergence_type: string;
  classification: 'NEEDS_INVESTIGATION' | 'COALESCE_EXPECTED';
}

export interface MonitoringStats {
  total_errors: number;
  total_problems: number;
  total_coalesce: number;
}

// Hook para buscar coletas com erro (skip_reason não nulo)
export const useCollectsErrors = (daysBack: number = 7) => {
  return useQuery({
    queryKey: ['collects-errors', daysBack],
    queryFn: async (): Promise<CollectError[]> => {
      const startDate = format(subDays(new Date(), daysBack), 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('collects_updates')
        .select(`
          collect_id,
          planned_date,
          sla_status,
          kind_of_material,
          skip_reason,
          created_at,
          establishment_name
        `)
        .not('skip_reason', 'is', null)
        .gte('created_at', startDate)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as CollectError[];
    },
    staleTime: 2 * 60 * 1000, // 2 minutos
  });
};

// Hook para buscar divergências (problemas + COALESCE)
export const useCollectsDivergences = () => {
  return useQuery({
    queryKey: ['collects-divergences'],
    queryFn: async (): Promise<CollectDivergence[]> => {
      // @ts-ignore - RPC function recently added via migration
      const { data, error } = await supabase.rpc('get_collects_divergences');

      if (error) throw error;
      
      // Processar e classificar divergências
      const divergences = (data as any[]).map(item => {
        // Identificar tipo de divergência
        let divergenceType = '';
        let classification: 'NEEDS_INVESTIGATION' | 'COALESCE_EXPECTED' = 'NEEDS_INVESTIGATION';

        if (item.current_status !== item.expected_status && item.expected_status !== null) {
          divergenceType = 'STATUS';
          classification = 'NEEDS_INVESTIGATION';
        } else if (item.current_weight !== item.expected_weight) {
          if (item.expected_weight === null && item.current_weight !== null) {
            divergenceType = 'COALESCE_WEIGHT';
            classification = 'COALESCE_EXPECTED';
          } else {
            divergenceType = 'WEIGHT';
            classification = 'NEEDS_INVESTIGATION';
          }
        } else if (item.current_volume !== item.expected_volume) {
          if (item.expected_volume === null && item.current_volume !== null) {
            divergenceType = 'COALESCE_VOLUME';
            classification = 'COALESCE_EXPECTED';
          } else {
            divergenceType = 'VOLUME';
            classification = 'NEEDS_INVESTIGATION';
          }
        } else if (item.current_units !== item.expected_units) {
          if (item.expected_units === null && item.current_units !== null) {
            divergenceType = 'COALESCE_UNITS';
            classification = 'COALESCE_EXPECTED';
          } else {
            divergenceType = 'UNITS';
            classification = 'NEEDS_INVESTIGATION';
          }
        }

        return {
          collect_id: item.collect_id,
          planned_date: item.planned_date,
          kind_of_material: item.kind_of_material,
          establishment_name: item.establishment_name,
          current_status: item.current_status,
          expected_status: item.expected_status,
          current_weight: item.current_weight,
          expected_weight: item.expected_weight,
          current_volume: item.current_volume,
          expected_volume: item.expected_volume,
          current_units: item.current_units,
          expected_units: item.expected_units,
          divergence_type: divergenceType,
          classification
        };
      });

      return divergences;
    },
    staleTime: 2 * 60 * 1000,
  });
};

// Hook para estatísticas resumidas
export const useMonitoringStats = () => {
  const { data: errors } = useCollectsErrors();
  const { data: divergences } = useCollectsDivergences();

  const stats: MonitoringStats = {
    total_errors: errors?.length || 0,
    total_problems: divergences?.filter(d => d.classification === 'NEEDS_INVESTIGATION').length || 0,
    total_coalesce: divergences?.filter(d => d.classification === 'COALESCE_EXPECTED').length || 0,
  };

  return stats;
};
